<script lang="ts">
const SAFE_WIDTH = 40
</script>
<script lang="ts" setup>
import { type VNodeChild, ref, useTemplateRef, watch, onMounted } from 'vue';
import { pxOrValue, hasSlotContent } from '../../utils';
interface Props {
  resizable?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  resizable: false,
});
defineSlots<{
  left?: () => VNodeChild;
  right?: () => VNodeChild;
  headerStart?: () => VNodeChild;
  headerEnd?: () => VNodeChild;
  footerStart?: () => VNodeChild;
  footerEnd?: () => VNodeChild;
}>();
const leftColumnWidth = defineModel<number>('leftColumnWidth');
const containerRef = useTemplateRef('container');
const leftColumnRef = useTemplateRef('leftColumn');
const resizing = ref(false);
function applyWidthStyles(width?: number) {
  containerRef.value?.style.setProperty(
    '--grid-template-columns',
    `${typeof width === 'number' ? pxOrValue(width) : 'minmax(0, 1fr)'} auto minmax(0, 1fr)`,
  );
}

function fixWidth(width?: number) {
  if (typeof width !== 'number' || !containerRef.value) {
    return width;
  }
  const maxWidth = containerRef.value.offsetWidth - SAFE_WIDTH;
  return Math.max(SAFE_WIDTH, Math.min(maxWidth, width));
}
function onResizeHandleMouseDown(event: MouseEvent) {
  if (!props.resizable || event.button !== 0 || !containerRef.value) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const startX = event.clientX;
  const startWidth = leftColumnWidth.value ?? leftColumnRef.value?.offsetWidth ?? 0;
  let width: number | undefined = startWidth;
  let animationFrame: ReturnType<typeof requestAnimationFrame> | null = null;
  function onMouseMove(event: MouseEvent) {
    resizing.value = true;
    if (animationFrame) {
      return;
    }
    animationFrame = requestAnimationFrame(() => {
      width = fixWidth(startWidth + (event.clientX - startX));
      applyWidthStyles(width);
      animationFrame = null;
    });
  }
  function onMouseUp() {
    resizing.value = false;
    leftColumnWidth.value = width;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}
watch(leftColumnWidth, (value) => {
  applyWidthStyles(fixWidth(value));
});
onMounted(() => {
  applyWidthStyles(fixWidth(leftColumnWidth.value));
});
</script>

<template>
  <div
    data-ui-component="UiTwoColumnsContainer"
    :class="[$style.root, { [$style.resizing]: resizing }]"
  >
    <div
      v-if="hasSlotContent($slots.headerStart) || hasSlotContent($slots.headerEnd)"
      :class="$style.header"
    >
      <div :class="$style.headerStart">
        <slot name="headerStart" />
      </div>
      <div :class="$style.headerEnd">
        <slot name="headerEnd" />
      </div>
    </div>
    <div ref="container" :class="$style.body" data-testid="ui-two-columns-container-body">
      <div ref="leftColumn" :class="$style.column" data-testid="ui-two-columns-container-left">
        <slot name="left" />
      </div>
      <div
        :class="[$style.handle, { [$style.interactiveHandle]: props.resizable }]"
        :data-resizable="props.resizable ? 'true' : 'false'"
        data-testid="ui-two-columns-container-handle"
        @mousedown="onResizeHandleMouseDown($event)"
      />
      <div :class="$style.column">
        <slot name="right" />
      </div>
    </div>
    <div
      v-if="hasSlotContent($slots.footerStart) || hasSlotContent($slots.footerEnd)"
      :class="$style.footer"
    >
      <div :class="$style.footerStart">
        <slot name="footerStart" />
      </div>
      <div :class="$style.footerEnd">
        <slot name="footerEnd" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.resizing .body {
  cursor: col-resize;

  > * {
    pointer-events: none;
  }
}

.handle {
  width: 2px;
  background-color: var(--ui2-stroke-secondary, #e4e5e8);
  pointer-events: none;
}

.interactiveHandle {
  position: relative;
  cursor: col-resize;
  pointer-events: all;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -5px;
    width: 12px;
  }
}

.column {
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  height: 100%;
  overflow: hidden;
  min-height: 0;
  background: var(--ui2-background-primary, #fff);
}

.root {
  display: flex;
  flex-direction: column;
  background: var(--ui2-background-primary, #fff);
  border-radius: 12px;
  overflow: hidden;
  overflow-anchor: none;
  display: flex;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  border: 1px solid var(--ui2-stroke-tertiary, #f0f1f4);
  padding: 4px;
}

.header {
  padding: 8px;
}

.body {
  flex: 1 1 0%;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: var(--grid-template-columns, 1fr auto 1fr);
  min-height: 0;
  border-radius: 8px;
}

.footer {
  padding: 8px;
}

.header,
.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  box-sizing: border-box;
}

.headerStart,
.footerStart {
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
}
</style>
